import {
  Breakpoints,
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  compact,
  FontWeight,
  IconBookClosed,
  IconBriefcase,
  IconCasperTest,
  IconChevronRight,
  IconCode,
  IconEntertainment,
  IconGraduateHat,
  IconHeadset,
  IconMedicalCross,
  IconMoney,
  IconSearch,
  IconShapes,
  IconSparkle,
  IconTools,
  Menu,
  type MenuItemSection,
  Tag,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/router';
import {
  type FC,
  type FocusEventHandler,
  type KeyboardEventHandler,
  type PointerEventHandler,
  type SVGProps,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { GoalName } from '../__generated-gql-types__/globalTypes';
import { useAuth } from '../context/AuthContext';
import {
  APPLICANT_BASE_URL,
  APPLICANT_SIGNUP_URL,
  CAREER_CATEGORY_ID,
  CAREER_CATEGORY_SLUG,
  COLLEGE_CATEGORY_ID,
  COLLEGE_CATEGORY_SLUG,
  GRE_CATEGORY_ID,
  GRE_CATEGORY_SLUG,
  INVESTMENT_BANKING_CATEGORY_ID,
  INVESTMENT_BANKING_CATEGORY_SLUG,
  LAW_SCHOOL_CATEGORY_ID,
  LAW_SCHOOL_CATEGORY_SLUG,
  MANAGEMENT_CONSULTING_CATEGORY_ID,
  MANAGEMENT_CONSULTING_CATEGORY_SLUG,
  MASTERS_PROGRAMS_CATEGORY_ID,
  MASTERS_PROGRAMS_CATEGORY_SLUG,
  MBA_CATEGORY_ID,
  MBA_CATEGORY_SLUG,
  MEDICAL_SCHOOL_CATEGORY_ID,
  MEDICAL_SCHOOL_CATEGORY_SLUG,
} from '../utils/constants';
import { formatGoalDescription } from '../utils/formatters';
import { generateSearchPath } from '../utils/routing';
import { getUrlString } from '../utils/url';

import {
  type CategoryCtaCategoryFragment,
  useCategoryCtaDataQuery,
} from './__generated-gql-types__/CategoryCta.generated';
import { type SelectOptionType } from './inputs/selects';
import Select from './inputs/selects/Select';
import {
  SingleCategoryTypeahead,
  useCategoryTypeaheadQueryData,
} from './typeahead/CategoryTypeahead';

const GOAL_OPTIONS: Array<SelectOptionType<GoalName>> = [
  GoalName.SCHOOL,
  GoalName.CAREER,
  GoalName.TEST,
].map((goal) => ({ value: goal, label: formatGoalDescription(goal) }));

const POPULAR_CATEGORIES: CategoryCtaCategoryFragment[] = [
  {
    __typename: 'Category',
    slug: MBA_CATEGORY_SLUG,
    id: MBA_CATEGORY_ID,
    goal: GoalName.SCHOOL,
    name: 'MBA',
  },
  {
    __typename: 'Category',
    slug: CAREER_CATEGORY_SLUG,
    id: CAREER_CATEGORY_ID,
    goal: GoalName.CAREER,
    name: 'Career Development',
  },
  {
    __typename: 'Category',
    slug: MANAGEMENT_CONSULTING_CATEGORY_SLUG,
    id: MANAGEMENT_CONSULTING_CATEGORY_ID,
    goal: GoalName.CAREER,
    name: 'Management Consulting',
  },
  {
    __typename: 'Category',
    slug: GRE_CATEGORY_SLUG,
    id: GRE_CATEGORY_ID,
    goal: GoalName.TEST,
    name: 'GRE',
  },
  {
    __typename: 'Category',
    slug: MEDICAL_SCHOOL_CATEGORY_SLUG,
    id: MEDICAL_SCHOOL_CATEGORY_ID,
    goal: GoalName.SCHOOL,
    name: 'Medical School',
  },
  {
    __typename: 'Category',
    slug: COLLEGE_CATEGORY_SLUG,
    id: COLLEGE_CATEGORY_ID,
    goal: GoalName.SCHOOL,
    name: 'College',
  },
  {
    __typename: 'Category',
    slug: MASTERS_PROGRAMS_CATEGORY_SLUG,
    id: MASTERS_PROGRAMS_CATEGORY_ID,
    goal: GoalName.SCHOOL,
    name: 'Master’s Programs',
  },
  {
    __typename: 'Category',
    slug: LAW_SCHOOL_CATEGORY_SLUG,
    id: LAW_SCHOOL_CATEGORY_ID,
    goal: GoalName.SCHOOL,
    name: 'Law School',
  },
  {
    __typename: 'Category',
    slug: INVESTMENT_BANKING_CATEGORY_SLUG,
    id: INVESTMENT_BANKING_CATEGORY_ID,
    goal: GoalName.CAREER,
    name: 'Investment Banking',
  },
];

export const CategoryCta: FC = () => {
  const [selectedGoal, setSelectedGoal] = useState<Nullable<GoalName>>(null);
  const [selectedCategory, setSelectedCategory] =
    useState<Nullable<CategoryCtaCategoryFragment>>(null);

  const { currentUser } = useAuth();

  const searchUrl = `${APPLICANT_BASE_URL}${generateSearchPath({ goal: selectedCategory?.goal, categorySlug: selectedCategory?.slug, subCategorySlug: null })}`;

  const searchPageURL = selectedCategory
    ? currentUser
      ? searchUrl
      : getUrlString(APPLICANT_SIGNUP_URL, {
          goal: selectedCategory.goal.toLowerCase(),
          category: selectedCategory.slug,
          redirect_url: searchUrl,
        })
    : null;

  return (
    <div className="mt-12 w-full md:w-min">
      <div className="mt-12 flex flex-col space-y-2 rounded-xl border border-leland-gray-stroke p-2 shadow-md md:h-20 md:flex-row md:items-center md:space-x-2 md:space-y-0">
        <Select
          className="h-16 rounded-lg md:h-full"
          label="Goal"
          labelIsHidden
          placeholder="Select your goal"
          onChange={(g) => {
            setSelectedCategory(null);
            if (g) {
              setSelectedGoal(g);
            }
          }}
          value={selectedGoal}
          options={GOAL_OPTIONS}
        />
        <div className="md:h-full md:py-2">
          <div className="h-px w-full bg-leland-gray-stroke md:h-full md:w-px" />
        </div>
        <SingleCategoryTypeahead
          className="h-16 md:h-full md:min-w-[16.25rem]"
          label="Category"
          labelIsHidden
          placeholder="Select a category"
          goal={selectedGoal}
          value={selectedCategory}
          onChange={(category) => {
            if (category) {
              setSelectedCategory(category);
            }
          }}
          disabled={!selectedGoal}
          required
          customErrorMessage="Category is required"
        />
        <a
          className="whitespace-nowrap"
          href={searchPageURL ?? ''}
          id="category_cta--find_coaching"
        >
          <Button
            label="Find coaching"
            buttonColor={ButtonColor.PRIMARY}
            disabled={!searchPageURL}
            size={ButtonSize.LARGE}
            width={ButtonWidth.FULL_MOBILE}
          />
        </a>
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-1.5 gap-y-2 whitespace-nowrap">
        <p className="text-base font-medium text-leland-gray-dark">Popular:</p>
        {POPULAR_CATEGORIES.map((category) => (
          <Button
            key={category.id}
            buttonColor={ButtonColor.GRAY}
            size={ButtonSize.SMALL}
            label={category.name}
            rounded
            onClick={() => {
              setSelectedGoal(category.goal);
              setSelectedCategory(category);
            }}
            id="category_cta--select_category"
          />
        ))}
      </div>
    </div>
  );
};

const navCategoryItemIconMap: Record<string, FC<SVGProps<SVGSVGElement>>> = {
  Popular: IconSparkle,
  'School Admissions': IconGraduateHat,
  'Test Prep': IconCasperTest,
  General: IconShapes,
  Business: IconBriefcase,
  Product: IconTools,
  'Finance & Accounting': IconMoney,
  Development: IconCode,
  'Sales & Customer': IconHeadset,
  'Health & Medicine': IconMedicalCross,
  'Arts, Media, and Entertainment': IconEntertainment,
  'Law & Public Policy': IconBookClosed,
};

export const CategoryCtaV2: FC = () => {
  const router = useRouter();
  const isSm = useMediaQuery(Breakpoints.SM);
  const isLg = useMediaQuery(Breakpoints.LG);

  const { inputRef: ref, onOpenChange, inputProps } = useFocusScope();

  const { data } = useCategoryCtaDataQuery();

  const { onInputChange, options, rawQuery, loading } =
    useCategoryTypeaheadQueryData({});

  const { currentUser } = useAuth();

  const onSelect = (category: CategoryCtaCategoryFragment) => {
    const searchUrl = `${APPLICANT_BASE_URL}${generateSearchPath({ goal: category?.goal, categorySlug: category?.slug, subCategorySlug: null })}`;

    const searchPageURL = category
      ? currentUser
        ? searchUrl
        : getUrlString(APPLICANT_SIGNUP_URL, {
            goal: category.goal.toLowerCase(),
            category: category.slug,
            redirect_url: searchUrl,
          })
      : null;
    if (searchPageURL) {
      void router.push(searchPageURL);
    }
  };

  const categoryCtaItems: MenuItemSection =
    data?.navCategories
      .map(({ name, categories }) => {
        const Icon = navCategoryItemIconMap[name];
        if (!Icon) {
          return undefined;
        }

        return {
          label: name,
          CustomLeftIcon: ({ iconClassName }) => (
            <Icon
              className={`text-leland-gray-extra-light group-hover:text-leland-gray-dark ${iconClassName}`}
            />
          ),
          RightIcon: IconChevronRight,
          items: [
            categories.map((category) => ({
              label: category.name,
              onSelect: () => onSelect(category),
            })),
          ],
        };
      })
      .filter(compact) ?? [];

  return (
    <div className="relative w-full md:max-w-xl">
      <Menu
        asChild
        itemSections={[
          rawQuery
            ? options.length > 0
              ? options.map((option) => ({
                  ...option,
                  onSelect: () => onSelect(option.value),
                }))
              : [
                  {
                    label: 'No results found',
                    disabled: true,
                    onSelect: () => undefined,
                  },
                ]
            : categoryCtaItems,
        ]}
        onOpenChange={onOpenChange}
        fillParentWidth
        subMenusInPageView={!isLg}
        loading={loading}
      >
        <div className="mt-5 flex items-center space-x-3 rounded-xl border border-leland-gray-stroke px-5 py-6 text-lg shadow-xl focus-within:border-leland-gray-dark md:mt-6 md:h-20">
          <IconSearch className="pointer-events-none size-5 text-leland-gray-extra-light" />
          <input
            id="category_cta--search"
            type="text"
            className="flex-1 select-none outline-none placeholder:text-leland-gray-light focus:placeholder:text-leland-gray-extra-light"
            placeholder={
              isSm
                ? 'Search for coaching in any category'
                : 'Search for coaching'
            }
            ref={ref}
            onChange={(e) => onInputChange(e.target.value)}
            {...inputProps}
          />
          <IconChevronRight className="pointer-events-none size-6 text-leland-gray-dark" />
        </div>
      </Menu>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-1.5 gap-y-2 whitespace-nowrap">
        <p className="text-base font-medium text-leland-gray-dark">Popular:</p>
        {POPULAR_CATEGORIES.slice(0, !isSm ? 5 : undefined).map((category) => (
          <button
            key={category.id}
            onClick={() => {
              onSelect(category);
            }}
          >
            <Tag
              text={category.name}
              hoverable
              fontWeight={FontWeight.NORMAL}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Radix has pretty aggressive focus trapping, so this hooks allows us to use an input field with a dropdown menu
 */
const useFocusScope = () => {
  const isSm = useMediaQuery(Breakpoints.SM);
  const hoveredElement = useRef<Nullable<Element>>(null);
  const ref = useRef<HTMLInputElement>(null);
  const isEditing = useRef(false);
  const isOpen = useRef(false);

  useEffect(() => {
    const body = document.body;
    const onFocus = (e: FocusEvent) => {
      // Stop the radix focus trap so that we can focus the input.
      // Contains makes it so that this works even when we aren't clicking directly on the input
      if (hoveredElement.current?.contains(ref.current) || !isSm) {
        e.stopPropagation();
      }

      if (isEditing.current || e.target === ref.current) {
        ref.current?.focus();
      }
    };
    const onHover = (e: MouseEvent) => {
      hoveredElement.current = e.target as Element;
    };

    const onClick = (e: MouseEvent) => {
      if (isEditing.current && !isSm) {
        e.stopPropagation();
      }
    };
    // Radix has "focus trap", which means that it won't let an open dropdown menu ever lose focus.
    // This event solves for that by stopping the focus from bubbling to their document event listener.
    body.addEventListener('focusin', onFocus);
    // Track the hover event that is needed for focus
    body.addEventListener('mouseover', onHover);
    // Mobile version of mouseover
    body.addEventListener('touchstart', onHover);
    // On mobile, when you click to focus the input and it starts to scroll, a click event is triggered on the dropdown list, selecting an item.
    // This prevents that from happening (has to be capture so that we get it before the click event on the dropdown list)
    body.addEventListener('click', onClick, true);
    return () => {
      body.removeEventListener('focusin', onFocus);
      body.removeEventListener('mouseover', onHover);
      body.removeEventListener('touchstart', onHover);
      body.removeEventListener('click', onClick);
    };
  }, [ref, isSm]);

  const onOpenChange = useCallback(
    (value: boolean) => {
      if (value) {
        setTimeout(() => {
          // This puts the focus into the input when the menu opens
          ref.current?.focus();
        });
      }
      isOpen.current = value;
    },
    [ref, isOpen],
  );

  const onFocus: FocusEventHandler = () => {
    isEditing.current = true;
  };

  const onBlur: FocusEventHandler = () => {
    isEditing.current = false;
  };

  const onKeyDown: KeyboardEventHandler = (e) => {
    // Space key normally closes the menu, so disable that when we are editing
    if (document.activeElement === ref.current && e.code === 'Space') {
      e.stopPropagation();
    }
  };

  const onPointerDown: PointerEventHandler = (e) => {
    // Prevents the menu from closing when clicking on the input
    if (isOpen.current) {
      e.preventDefault();
      e.stopPropagation();
    }

    ref.current?.focus();

    // Scroll the input box to the top for mobile
    if (!isSm) {
      // Prevent default prevents an extra click in the category list
      ref.current?.parentElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      // When we scroll, the radix focus trap will cause the input to lose focus. But it only works to focus it back after a short delay.
      setTimeout(() => {
        ref.current?.focus();
      }, 500);
    }
  };

  return {
    inputRef: ref,
    onOpenChange,
    inputProps: {
      onFocus,
      onBlur,
      onKeyDown,
      onPointerDown,
    },
  };
};
